/**
 * Login Page
 * Admin user authentication interface
 */

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  InputAdornment,
  IconButton,
  Link,
  Divider,
  Chip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Email as EmailIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { APP_ENV } from '../../constants/config';
import { AuthService } from '../../services/auth/AuthService';
import { Card, CardContent, TextField, PrimaryButton } from '../../components/ui';
import iconImage from '../../assets/icon.png';

const LoginPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const showTestAccounts = APP_ENV === 'development';
  // Default super admin credentials for development/demo
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});

  // Clear any stale authentication data on mount
  React.useEffect(() => {
    // Clear all auth-related data when login page loads
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  }, []);

  const validateForm = (): boolean => {
    const newErrors: { email?: string; password?: string } = {};
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setIsLoading(true);
      await AuthService.login({ email: email.trim(), password });
      // Navigate to the page user was trying to access, or dashboard
      const from = (location.state as { from?: Location })?.from?.pathname || '/';
      navigate(from, { replace: true });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Invalid email or password';
      setErrors({ password: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  // Test accounts for development/demo
  const testAccounts = showTestAccounts
    ? [
        {
          name: 'Admin',
          email: 'admin@mispy.ai',
          password: 'P@ssword!',
          role: 'super_admin',
          description: 'Can manage Settings and admin users',
        }
      ]
    : [];

  const handleUseTestAccount = (testEmail: string, testPassword: string) => {
    setEmail(testEmail);
    setPassword(testPassword);
    setErrors({});
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.background.default,
        padding: theme.spacing(3),
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: 480,
          boxShadow: theme.shapes.shadow.lg,
        }}
      >
        <CardContent
          sx={{
            padding: theme.spacing(4),
            '&:last-child': {
              paddingBottom: theme.spacing(4),
            },
          }}
        >
          {/* Logo and Title */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              marginBottom: theme.spacing(4),
            }}
          >
            <Box
              component="img"
              src={iconImage}
              alt="MiSpy.ai Logo"
              sx={{
                width: 96,
                height: 96,
                borderRadius: theme.shapes.borderRadius.md,
                marginBottom: theme.spacing(2),
                objectFit: 'contain',
              }}
            />
            <Typography
              variant="h4"
              sx={{
                fontWeight: theme.typography.h4.fontWeight,
                color: theme.palette.text.primary,
                marginBottom: theme.spacing(0.5),
              }}
            >
              MiSpy.ai Admin
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
              }}
            >
              Sign in to your account
            </Typography>
          </Box>

          {/* Login Form */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
            <TextField
              label="Email Address"
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) {
                  setErrors({ ...errors, email: undefined });
                }
              }}
              onKeyPress={handleKeyPress}
              error={!!errors.email}
              helperText={errors.email}
              fullWidth
              autoComplete="email"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ color: theme.palette.text.secondary }} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Password"
              placeholder="Enter your password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (errors.password) {
                  setErrors({ ...errors, password: undefined });
                }
              }}
              onKeyPress={handleKeyPress}
              error={!!errors.password}
              helperText={errors.password}
              fullWidth
              autoComplete="current-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: theme.palette.text.secondary }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Link
                component="button"
                variant="body2"
                onClick={() => {
                  // TODO: Implement forgot password
                  console.log('Forgot password');
                }}
                sx={{
                  color: theme.palette.primary.main,
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                  cursor: 'pointer',
                }}
              >
                Forgot Password?
              </Link>
            </Box>

            <PrimaryButton
              fullWidth
              onClick={handleLogin}
              disabled={isLoading}
              sx={{
                padding: theme.spacing(1.5),
                fontSize: theme.typography.body1.fontSize,
              }}
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </PrimaryButton>
          </Box>

          {showTestAccounts && (
            <>
              {/* Test Accounts Section */}
              <Box sx={{ marginTop: theme.spacing(4) }}>
                <Divider sx={{ marginBottom: theme.spacing(2) }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Test Accounts
                  </Typography>
                </Divider>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1.5) }}>
                  {testAccounts.map((account) => (
                    <Box
                      key={account.email}
                      sx={{
                        padding: theme.spacing(1.5),
                        borderRadius: 0,
                        backgroundColor: theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.05)'
                          : 'rgba(0, 0, 0, 0.02)',
                        border: `1px solid ${theme.customColors?.tableBorder || theme.palette.divider}`,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.08)'
                            : 'rgba(0, 0, 0, 0.04)',
                          borderColor: theme.palette.primary.main,
                        },
                      }}
                      onClick={() => handleUseTestAccount(account.email, account.password)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: theme.spacing(0.5) }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1), marginBottom: theme.spacing(0.5) }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                              }}
                            >
                              {account.name}
                            </Typography>
                            <Chip
                              label={account.role === 'super_admin' ? 'Super Admin' : 'Support'}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.7rem',
                                backgroundColor: account.role === 'super_admin'
                                  ? theme.palette.primary.main
                                  : theme.palette.secondary.main,
                                color: theme.palette.common.white,
                              }}
                            />
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.palette.text.secondary,
                              fontSize: '0.75rem',
                            }}
                          >
                            {account.description}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ marginTop: theme.spacing(1), display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}>
                          <EmailIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.palette.text.primary,
                              fontFamily: 'monospace',
                              fontSize: '0.75rem',
                            }}
                          >
                            {account.email}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}>
                          <LockIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.palette.text.primary,
                              fontFamily: 'monospace',
                              fontSize: '0.75rem',
                            }}
                          >
                            {account.password}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    marginTop: theme.spacing(1.5),
                    color: theme.palette.text.secondary,
                    fontSize: '0.7rem',
                    textAlign: 'center',
                    fontStyle: 'italic',
                  }}
                >
                  Click on an account card to fill the login form
                </Typography>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default LoginPage;
